"use client";
import { FornecedorCallbacks, FornecedorRaw } from "@apiInclui/fornecedor";
import { CalendarIcon, Loader2, Save, Search } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCatalogs, useUserStore } from "store";
import { usePrenotaStore } from "@PN/inclusao";
import { FornecedorDialog } from "@PN/_nucleo";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useTransition, useState } from "react";
import { format } from "date-fns";
import {
  Button,
  Calendar,
  Card,
  CardContent,
  cn,
  Combobox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  ScrollArea,
} from "visuais";
import {
  AnexosFatura,
  useFaturasStore,
  useAnexoFinanceiro,
  AnexoFinanceiroIdentificador,
  useSalvarFatura,
  ProgressBar,
  filtroFaturasSchema,
  FiltroFaturasFormValues,
} from "@FN";

export function FiltroFaturasForm({
  mode = "inclusao",
  onPesquisar,
}: {
  mode?: "inclusao" | "edicao" | "visualizar";
  onPesquisar: () => void;
}) {
  const [isPending, startTransition] = useTransition();
  const anexos = usePrenotaStore((state) => state.draft.anexos);
  const addFileToAux = usePrenotaStore((state) => state.actions.aux.files.add);
  const addAnexoToDraft = usePrenotaStore(
    (state) => state.actions.draft.anexos.add
  );

  const {
    fornecedores,
    setFornecedores,
    filial,
    setFilial,
    date,
    setDate,
    setDataInicial,
    setDataFinal,
    fornecedorFatura,
    setFornecedorFatura,
    faturas,
    setFaturas,
    table,
    isLoading,
    observacao,
    setObservacao,
    rec,
    faturaZ15,
  } = useFaturasStore();
  const usuario = useUserStore().user;
  // Carregar anexos existentes no modo edição
  const faturaSelecionada = faturas.find((f) => f.R_E_C_N_O_ === rec);

  const identificador: AnexoFinanceiroIdentificador | null = (() => {
    // No modo edição, usamos os dados Z15 do store
    if (mode === "edicao" && faturaZ15) {
      return {
        filial: faturaZ15.Z15_FILIAL,
        fornecedor: faturaZ15.Z15_FORNEC,
        loja: faturaZ15.Z15_LOJA,
        cod: faturaZ15.Z15_COD,
      };
    }

    // No modo inclusão, usa faturaSelecionada se disponível
    return faturaSelecionada
      ? {
          filial: faturaSelecionada.Z15_FILIAL,
          fornecedor: faturaSelecionada.Z15_FORNEC,
          loja: faturaSelecionada.Z15_LOJA,
          cod: faturaSelecionada.Z15_COD,
        }
      : null;
  })();

  const { data: anexosRemotos, refetch: refetchAnexos } =
    useAnexoFinanceiro(identificador);

  useEffect(() => {
    if (mode !== "edicao") return;
    if (!rec) return;
    if (!faturaZ15) return; // Aguarda os dados Z15 serem carregados
    if ((anexos?.length || 0) > 0) return; // já populado

    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await refetchAnexos();
        const lista = data || anexosRemotos || [];
        if (!lista || lista.length === 0) return;

        for (let i = 0; i < lista.length; i++) {
          const d = lista[i];
          const url = d.fileData.fileUrl;
          const name = d.fileData.fileName;
          const type = d.fileData.fileType || "application/pdf";
          const res = await fetch(url);
          if (!res.ok) continue;
          const blob = await res.blob();
          if (cancelled) return;
          const file = new File([blob], name, { type });

          // adiciona arquivo à store auxiliar (mantém nome para matching no save)
          addFileToAux(file);

          // adiciona entrada no draft para edição
          const item = String(i + 1).padStart(3, "0");
          addAnexoToDraft({ item, nomeArquivo: name, observacao: "" });
        }
      } catch (e) {
        console.error("Erro ao carregar anexos no modo edição:", e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rec, faturaZ15]);

  // integração react-hook-form + zod
  const form = useForm<FiltroFaturasFormValues>({
    resolver: zodResolver(filtroFaturasSchema),
    defaultValues: {
      fornecedores: [],
      filial: "",
      date: null,
      dataInicial: "",
      dataFinal: "",
      fornecedorFatura: null,
      faturas: [],
      observacao: "",
      anexos: [],
      rec: 0,
    },
  });

  const progress = useMemo(() => {
    const selecionados = faturas.filter((f) => f.checked);

    const requirements = [
      { completed: fornecedores.length > 0, name: "fornecedores" },
      { completed: !!filial, name: "filial" },
      { completed: !!fornecedorFatura, name: "fornecedorFatura" },
      { completed: selecionados.length > 0, name: "faturas" },
      { completed: anexos.length > 0, name: "anexos" },
    ];

    const completedCount = requirements.filter((req) => req.completed).length;
    return (completedCount / requirements.length) * 100;
  }, [fornecedores, filial, fornecedorFatura, faturas, anexos]);

  // Sincronizar o estado do formulário com o store
  useEffect(() => {
    form.setValue("fornecedores", fornecedores);
  }, [fornecedores, form]);

  useEffect(() => {
    form.setValue("filial", filial);
  }, [filial, form]);

  useEffect(() => {
    form.setValue("fornecedorFatura", fornecedorFatura);
  }, [fornecedorFatura, form]);

  useEffect(() => {
    form.setValue("faturas", faturas);
  }, [faturas, form]);

  useEffect(() => {
    form.setValue("observacao", observacao || "");
  }, [observacao, form]);

  // Sincronizar estado inicial do formulário
  useEffect(() => {
    form.reset({
      fornecedores,
      filial,
      date: null,
      dataInicial: "",
      dataFinal: "",
      fornecedorFatura,
      faturas,
      observacao: observacao || "",
      anexos: [],
      rec: rec || 0,
    });
  }, [faturas, filial, form, fornecedorFatura, fornecedores, observacao, rec]); // Só executa uma vez na montagem

  const dialogCallbacks = {
    onFornecedorSelect: (forn: FornecedorRaw, cb?: FornecedorCallbacks) => {
      if (!forn.A2_COD || !forn.A2_LOJA) return;
      setFornecedores((prev: FornecedorRaw[]) => {
        if (
          prev.some(
            (f) => f.A2_COD === forn.A2_COD && f.A2_LOJA === forn.A2_LOJA
          )
        )
          return prev;
        return [...prev, forn];
      });
      cb?.onAfterSelect?.(forn);
    },
    onSearchResultsUpdate: () => {},
    onClearAll: () => {},
  };

  const { filiais } = useCatalogs();
  const fornecedorItems = useMemo(
    () =>
      fornecedores.map((f) => ({
        value: `${f.A2_COD}-${f.A2_LOJA}`,
        label: `${f.A2_NOME}\nCNPJ: ${f.A2_CGC}`,
      })),
    [fornecedores]
  );
  const numeroNFItems = useMemo(
    () =>
      faturas
        .filter((f) => !f.checked)
        .map((f) => {
          const key = String(
            f.R_E_C_N_O_ ?? `${f.F1_FILIAL}-${f.F1_DOC}-${f.F1_SERIE}-${f.F1_DOC}-${f.Z10_CHAVE}`
          );
          const cod = String(f.F1_DOC || "").replace(/^0+/, "");
          const emis = String(f.F1_EMISSAO || "");
          const emissaoFmt = emis && emis.length === 8
            ? `${emis.slice(6,8)}/${emis.slice(4,6)}/${emis.slice(0,4)}`
            : emis;
          return { value: key, label: `${cod} - ${emissaoFmt}` };
        }),
    [faturas]
  );

  const selecionados = faturas.filter((f) => f.checked);
  const fornecedorSelecionado =
    fornecedorFatura?.A2_COD && fornecedorFatura?.A2_LOJA;
  // Permite filiais diferentes entre as faturas selecionadas

  useEffect(() => {
    if (fornecedores.length === 0) {
      setFaturas([]);
      table?.setOptions((prev) => ({ ...prev, data: [] }));
    }
  }, [fornecedores, setFaturas, table]);

  const { handleSalvar, salvarFaturaMutation } = useSalvarFatura({
    mode,
    rec,
    anexos,
    fornecedorFatura,
    selecionados,
    observacao,
    usuario,
    filial: filial,
    table,
    form,
    fornecedorSelecionado,
    faturaZ15
  });

  const pendingItems = useMemo(() => {
    const items = [];
    if (!fornecedores.length) items.push("Selecione ao menos um fornecedor");
    if (!filial) items.push("Selecione uma filial");
    if (!fornecedorFatura) items.push("Selecione um fornecedor para a fatura");
    if (selecionados.length === 0) items.push("Selecione ao menos uma fatura");
    if (anexos.length === 0) items.push("Selecione pelo menos um anexo");
    return items;
  }, [
    fornecedores,
    filial,
    fornecedorFatura,
    selecionados,
    anexos,
  ]);
  
  return (
    <Card>
      <CardContent>
        <Form {...form}>
          <form onSubmit={handleSalvar}>
            <div className="mb-4">
              <ProgressBar
                progress={progress}
                showPercentage={false}
                pendingItems={pendingItems}
                variant="pulse"
                size="md"
                animated={true}
              />
            </div>
            <fieldset className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" disabled={mode === "edicao" && progress < 100}>
              {/* Fornecedores */}
              <FormField
                control={form.control}
                name="fornecedores"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <FormLabel>Fornecedores</FormLabel>

                      {fornecedores.length > 0 && (
                        <Popover modal>
                        <PopoverTrigger asChild>
                          <div className="inline-flex items-center px-2 bg-primary text-primary-foreground rounded cursor-pointer select-none text-xs">
                            {fornecedores.length} selecionado
                            {fornecedores.length > 1 ? "s" : ""} ▾
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2 overflow-hidden">
                          <div className="flex flex-col h-80">
                            {/* Área rolável */}
                            <ScrollArea className="flex-1 pr-1 h-70">
                              <div className="flex flex-col gap-2">
                                {fornecedores.map((f) => (
                                  <div
                                    key={`${f.A2_COD ?? "X"}-${f.A2_LOJA ?? "Y"}`}
                                    className="flex justify-between items-center p-1 border rounded"
                                  >
                                    <span className="text-sm">{f.A2_NOME}</span>
                                    {mode !== "visualizar" && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => {
                                          const novosFornecedores = fornecedores.filter(
                                            (p) => !(p.A2_COD === f.A2_COD && p.A2_LOJA === f.A2_LOJA)
                                          );
                                          setFornecedores(novosFornecedores);
                                          field.onChange(novosFornecedores);
                                        }}
                                      >
                                        ✕
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                      
                            {/* Rodapé fixo */}
                            {mode !== "visualizar" && (
                              <div className="shrink-0 mt-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full h-8 text-xs"
                                  onClick={() => {
                                    setFornecedores([]);
                                    field.onChange([]);
                                  }}
                                >
                                  Remover todos
                                </Button>
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      )}
                    </div>

                    <FormControl>
                      <FornecedorDialog
                        disabled={mode==="visualizar"}
                        current={undefined}
                        callbacks={{
                          onFornecedorSelected: (item) => {
                            const novoFornecedor = {
                              A2_COD: item.cod,
                              A2_LOJA: item.loja,
                              A2_NOME: item.nome,
                              A2_CGC: item.cnpj,
                            };
                            const novosFornecedores = [
                              ...fornecedores,
                              novoFornecedor,
                            ];
                            startTransition(() => {
                              setFornecedores(novosFornecedores);
                              field.onChange(novosFornecedores);
                            });
                            dialogCallbacks.onFornecedorSelect(novoFornecedor);
                          },
                        }}
                        dialogCallbacks={dialogCallbacks}
                        multiselect={true}
                        selectedFornecedores={fornecedores}
                        onSelectAll={(novosFornecedores) => {
                          startTransition(() => {
                            setFornecedores(novosFornecedores);
                            field.onChange(novosFornecedores);
                          });
                        }}
                        showSelectedIndicator={true}
                        maxDialogItems={40}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Fornecedor Fatura */}
              <div className="space-y-2">
                <Label htmlFor="fornecedorFatura">Fornecedor Fatura</Label>
                <Combobox
                  disabled={mode==="visualizar"}
                  modal
                  items={fornecedorItems}
                  selectedValue={
                    fornecedorFatura?.A2_COD && fornecedorFatura?.A2_LOJA
                      ? `${fornecedorFatura.A2_COD}-${fornecedorFatura.A2_LOJA}`
                      : ""
                  }
                  onSelect={(value) => {
                    const [cod, loja] = String(value).split("-");
                    const selecionado = fornecedores.find(
                      (f) => f.A2_COD === cod && f.A2_LOJA === loja
                    );
                    startTransition(() => setFornecedorFatura(selecionado || null));
                  }}
                  placeholder="Selecione um fornecedor"
                />
              </div>

              {/* Número NF */}
              <div className="space-y-2">
                <Label htmlFor="numeroNF">Número NF</Label>
                <Combobox
                  disabled={mode==="visualizar"}
                  items={numeroNFItems}
                  selectedValue={""}
                  listHeader={<div>Código - Emissão</div>}
                  onSelect={(value) => {
                    startTransition(() => {
                      const nova = faturas.map((f: any) => {
                        const key = String(
                          f.R_E_C_N_O_ ?? `${f.F1_FILIAL}-${f.F1_DOC}-${f.F1_SERIE}-${f.F1_DOC}-${f.Z10_CHAVE}`
                        );
                        return key === value ? { ...f, checked: true } : f;
                      });
                      setFaturas(nova);
                    });
                  }}
                  placeholder="Selecione a fatura"
                  modal={true}
                  closeOnSelect={false}
                />
              </div>

              {/* Anexos */}
              <div className="flex-1 flex max-w-lg items-center gap-2">
                <FormField
                  control={form.control}
                  name="anexos"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anexos</FormLabel>
                      <FormControl>
                        <AnexosFatura mode={mode}/>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              {/* Filial */}
              <FormField
                control={form.control}
                name="filial"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Filial</FormLabel>
                    <FormControl>
                      <Combobox
                        items={filiais.options}
                        selectedValue={filial || ""}
                        onSelect={(value) => {
                          field.onChange(value || "");
                          setFilial(value || "");
                        }}
                        placeholder="Selecione a filial"
                        modal={true}
                        disabled={mode === "edicao" || mode === "visualizar"}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Período */}
              <div className="space-y-2">
                <Label htmlFor="periodo">Período Emissão</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      disabled={mode==="visualizar"}
                      id="date-range"
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !date?.from && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date?.from ? (
                        date.to ? (
                          `${format(date.from, "LLL dd, y")} - ${format(
                            date.to,
                            "LLL dd, y"
                          )}`
                        ) : (
                          format(date.from, "LLL dd, y")
                        )
                      ) : (
                        <span>Selecione um período</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      defaultMonth={date?.from}
                      selected={date}
                      onSelect={(range) => {
                        setDate(range);
                        setDataInicial(
                          range?.from ? format(range.from, "yyyyMMdd") : ""
                        );
                        setDataFinal(
                          range?.to ? format(range.to, "yyyyMMdd") : ""
                        );
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Observação */}
              <FormField
                control={form.control}
                name="observacao"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Observação</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Observação..."
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => {
                          if (mode !== "visualizar") {
                            field.onChange(e.target.value);
                            setObservacao(e.target.value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Botões */}
              <div className="flex justify-between gap-4 mt-4">
                {/* Pesquisar */}
                <div className="flex gap-2">
                  {mode === "edicao" &&(
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-38 mt-2 cursor-pointer border bg-gradient-to-r from-slate-200 via-slate-300 to-gray-200 border-gray-400
                        dark:from-slate-600 dark:via-slate-500 dark:to-gray-600"
                      disabled={
                        !fornecedorSelecionado ||
                        !filial ||
                        selecionados.length === 0
                      }
                      onClick={onPesquisar}
                    >
                      {isLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Search className="size-4" />
                      )}
                      Pesquisar
                    </Button>
                  )}

                  {/* Salvar */}
                  <Button
                    type="submit"
                    variant="outline"
                    className="mt-2 w-38 relative overflow-hidden cursor-pointer"
                    disabled={
                      salvarFaturaMutation.isPending || 
                      !fornecedorSelecionado ||
                      !filial ||
                      selecionados.length === 0 ||
                      anexos.length === 0 ||
                      (mode === "edicao" && !faturaZ15) ||
                      mode === "visualizar"
                    }
                  >
                    {/* fundo preenchido conforme progresso */}
                    <div
                      className={cn(
                        "absolute left-0 top-0 h-full transition-all duration-500",
                        progress === 100
                          ? "bg-gradient-to-r border border-green-600 from-green-400 via-teal-400 to-emerald-400 dark:from-green-500 dark:via-teal-500 dark:to-emerald-500"
                          : "bg-gradient-to-r from-blue-500 via-cyan-500 to-sky-500"
                      )}
                      style={{ width: `${progress}%` }}
                    />


                    {/* conteúdo do botão */}
                    <span className="flex items-center gap-2 relative z-10">
                      {salvarFaturaMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Save className="size-4" />
                      )}
                      {salvarFaturaMutation.isPending ? "Salvando..." : "Salvar"}
                    </span>
                  </Button>
                </div>
              </div>
            </fieldset>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
